import type { FormEvent } from "react";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Dismiss20Regular,
  Save20Regular,
} from "@fluentui/react-icons";
import { PageHeader } from "../../components/PageHeader";
import {
  ApiError,
  getWarehouseById,
  updateWarehouse,
  type WarehousePayload,
} from "../../armazens/armazensApi";
import { APP_LOCALE } from "../../config/locale";

const parseErrorMessage = (error: unknown): string => {
  if (error instanceof ApiError) {
    if (error.status === 401) {
      return "Sessão expirada. Por favor, autentique-se novamente.";
    }
    if (error.status === 403) {
      return "Sem permissões para editar armazéns.";
    }
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  return "Não foi possível atualizar o armazém.";
};

const parseLoadError = (error: unknown): string => {
  if (error instanceof ApiError) {
    if (error.status === 401) {
      return "Sessão expirada. Por favor, autentique-se novamente.";
    }
    if (error.status === 403) {
      return "Sem permissões para consultar armazéns.";
    }
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  return "Não foi possível carregar o armazém.";
};

const formatDate = (value?: string | null): string => {
  if (!value) return "—";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "—";
  return parsed.toLocaleDateString(APP_LOCALE);
};

export function EditarArmazemView() {
  const navigate = useNavigate();
  const { warehouseId } = useParams();
  const [nomeArmazem, setNomeArmazem] = useState("");
  const [codigo, setCodigo] = useState("");
  const [morada, setMorada] = useState("");
  const [responsavel, setResponsavel] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [lastUpdatedLabel, setLastUpdatedLabel] = useState<string | null>(null);
  const [warehouseSnapshotId, setWarehouseSnapshotId] = useState<number | null>(null);

  useEffect(() => {
    let isMounted = true;

    const loadWarehouse = async () => {
      if (!warehouseId) {
        setLoadError("Armazém inválido.");
        setIsLoading(false);
        return;
      }

      const parsedId = Number(warehouseId);
      if (Number.isNaN(parsedId)) {
        setLoadError("Armazém inválido.");
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setLoadError(null);
      setSubmitError(null);

      try {
        const response = await getWarehouseById(parsedId);
        if (!isMounted) return;

        setWarehouseSnapshotId(response.id);
        setNomeArmazem(response.name ?? "");
        setCodigo(response.code ?? "");
        setMorada(response.address ?? "");
        setResponsavel(response.responsible ?? "");
        setLastUpdatedLabel(formatDate(response.lastModified));
      } catch (error) {
        if (!isMounted) return;
        setLoadError(parseLoadError(error));
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadWarehouse();

    return () => {
      isMounted = false;
    };
  }, [warehouseId]);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setSubmitError(null);

    if (!warehouseSnapshotId) {
      setSubmitError("Armazém inválido.");
      return;
    }

    if (!nomeArmazem.trim()) {
      setSubmitError("A designação do armazém é obrigatória.");
      return;
    }

    setIsSaving(true);

    const payload: WarehousePayload = {
      name: nomeArmazem.trim(),
      code: codigo.trim() || null,
      address: morada.trim() || null,
      responsible: responsavel.trim() || null,
    };

    try {
      await updateWarehouse(warehouseSnapshotId, payload);
    } catch (error) {
      setSubmitError(parseErrorMessage(error));
      setIsSaving(false);
      return;
    }

    setIsSaving(false);
    navigate("/armazens", {
      state: { toast: "Armazém atualizado com sucesso" },
    });
  };

  const actions = (
    <div className="flex items-center gap-4">
      <button
        className="px-4 py-2 bg-secondary hover:bg-accent border border-border rounded transition-colors flex items-center gap-2 min-h-[44px]"
        style={{ borderRadius: "var(--radius-button)" }}
        onClick={() => navigate("/armazens")}
        disabled={isSaving}
      >
        <Dismiss20Regular className="w-5 h-5" />
        <span className="hidden sm:inline">Cancelar</span>
      </button>
      <button
        className="px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded transition-colors flex items-center gap-2 min-h-[44px]"
        style={{
          borderRadius: "var(--radius-button)",
          height: "var(--button-height)",
        }}
        onClick={() => {
          const form = document.getElementById(
            "editar-armazem-form",
          ) as HTMLFormElement | null;
          form?.requestSubmit();
        }}
        disabled={isSaving || isLoading}
      >
        <Save20Regular className="w-5 h-5" />
        {isSaving ? "A guardar..." : "Guardar Alterações"}
      </button>
    </div>
  );

  return (
    <div className="flex flex-col h-full">
      <PageHeader
        title="Editar Armazém"
        subtitle="Atualize os dados do armazém."
        actions={actions}
        backButton={{ onClick: () => navigate("/armazens") }}
      />

      <div className="flex-1 overflow-y-auto bg-background">
        <div className="p-4 md:p-6">
          {loadError && (
            <div
              className="bg-card border border-[var(--destructive)] px-4 py-3 mb-6 text-[var(--destructive)]"
              style={{ borderRadius: "var(--radius-card)" }}
            >
              {loadError}
            </div>
          )}

          {submitError && (
            <div
              className="bg-card border border-[var(--destructive)] px-4 py-3 mb-6 text-[var(--destructive)]"
              style={{ borderRadius: "var(--radius-card)" }}
            >
              {submitError}
            </div>
          )}

          <div
            className="text-secondary-text mb-6"
            style={{
              fontSize: "var(--text-caption)",
              fontWeight: "var(--font-weight-normal)",
            }}
          >
            Última atualização: {lastUpdatedLabel ?? "—"}
          </div>

          <form id="editar-armazem-form" onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-20">
              <div className="space-y-6">
                <div
                  className="bg-card border border-border p-6 space-y-5"
                  style={{ borderRadius: "var(--radius-card)" }}
                >
                  <h3
                    className="text-foreground"
                    style={{
                      fontSize: "var(--text-section-title)",
                      fontWeight: "var(--font-weight-semi-bold)",
                    }}
                  >
                    Dados do Armazém
                  </h3>

                  <div>
                    <label
                      htmlFor="nomeArmazem"
                      className="block text-foreground mb-2"
                      style={{
                        fontSize: "var(--text-caption)",
                        fontWeight: "var(--font-weight-semi-bold)",
                      }}
                    >
                      Designação{" "}
                      <span className="text-primary">*</span>
                    </label>
                    <input
                      id="nomeArmazem"
                      type="text"
                      value={nomeArmazem}
                      onChange={(event) => setNomeArmazem(event.target.value)}
                      className="w-full px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                      style={{
                        borderRadius: "var(--radius-button)",
                        fontSize: "var(--text-base)",
                      }}
                      disabled={isLoading}
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="codigo"
                      className="block text-foreground mb-2"
                      style={{
                        fontSize: "var(--text-caption)",
                        fontWeight: "var(--font-weight-semi-bold)",
                      }}
                    >
                      Identificador Interno
                    </label>
                    <input
                      id="codigo"
                      type="text"
                      value={codigo}
                      onChange={(event) => setCodigo(event.target.value)}
                      className="w-full px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                      style={{
                        borderRadius: "var(--radius-button)",
                        fontSize: "var(--text-base)",
                      }}
                      disabled={isLoading}
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="morada"
                      className="block text-foreground mb-2"
                      style={{
                        fontSize: "var(--text-caption)",
                        fontWeight: "var(--font-weight-semi-bold)",
                      }}
                    >
                      Morada
                    </label>
                    <input
                      id="morada"
                      type="text"
                      value={morada}
                      onChange={(event) => setMorada(event.target.value)}
                      className="w-full px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                      style={{
                        borderRadius: "var(--radius-button)",
                        fontSize: "var(--text-base)",
                      }}
                      disabled={isLoading}
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="responsavel"
                      className="block text-foreground mb-2"
                      style={{
                        fontSize: "var(--text-caption)",
                        fontWeight: "var(--font-weight-semi-bold)",
                      }}
                    >
                      Responsável
                    </label>
                    <input
                      id="responsavel"
                      type="text"
                      value={responsavel}
                      onChange={(event) => setResponsavel(event.target.value)}
                      className="w-full px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                      style={{
                        borderRadius: "var(--radius-button)",
                        fontSize: "var(--text-base)",
                      }}
                      disabled={isLoading}
                    />
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
